import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { nextStepTestName } from "../../shared/stepTests/constants";
import { pinContentIssue } from "../lib/pinEligibility";
import { useExecutionContent } from "./useExecutionContent";
import { useNavigateOnBranchChange } from "./useNavigateOnBranchChange";
import { useStepExecutions } from "./useStepExecutions";

export type StepTestFixtureSetupRequest = {
  stepDir: string;
  executionId: string;
  name: string;
};

// Logic for pinning a successful, not-yet-pinned run of a step as a new step
// test. Returns the picker state and a `message` for the cases where pinning
// isn't possible, so callers can place the picker and preview wherever they
// like (e.g. the Testing tab puts the picker in the step heading).
export function useStepTestPin({
  stepDir,
  workflowId,
  branchId,
  commitId,
  isMainBranch,
  hasStorage,
  takenNames,
  pinnedExecutionIds,
  onAskSetupStepTest,
}: {
  stepDir: string;
  workflowId: string;
  branchId: string;
  commitId: string | null;
  isMainBranch: boolean;
  hasStorage: boolean;
  takenNames: readonly string[];
  pinnedExecutionIds: ReadonlySet<string>;
  onAskSetupStepTest?: (request: StepTestFixtureSetupRequest) => void;
}) {
  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);
  const { executions } = useStepExecutions(workflowId, branchId, stepDir);
  const successfulRuns = executions.filter(
    (e) => e.kind === "run" && e.status === "success"
  );
  const runs = successfulRuns.filter((r) => !pinnedExecutionIds.has(r.id));

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedRun = runs.find((r) => r.id === selectedId) ?? null;

  const output = useExecutionContent(selectedRun ?? undefined, "output");
  const input = useExecutionContent(selectedRun ?? undefined, "input");

  const issue = pinContentIssue(output, input);
  const canPin = selectedRun != null && !isMainBranch && issue === null;
  const pinTooltip =
    issue ??
    "Pin this run’s input and output as a step test. Future checks replay the input and expect the same output.";

  const handlePin = () => {
    if (!canPin || !commitId || !selectedRun) return;
    const name = nextStepTestName(takenNames);
    if (onAskSetupStepTest && hasStorage) {
      onAskSetupStepTest({ stepDir, executionId: selectedRun.id, name });
      setSelectedId(null);
      return;
    }
    void navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.pinStepTest({
          workflowId,
          ...branchParams,
          stepDir,
          executionId: selectedRun.id,
          name,
          input: input.content ?? "",
          expectedOutput: output.content ?? "",
        })
      )
    );
    setSelectedId(null);
  };

  const message = isMainBranch
    ? "Switch to a branch to pin step tests."
    : runs.length === 0
      ? successfulRuns.length === 0
        ? "Run this step to pin a step test."
        : "Every successful run is already pinned."
      : null;

  return {
    runs,
    selectedId,
    setSelectedId,
    selectedRun,
    output,
    canPin,
    pinTooltip,
    handlePin,
    message,
  };
}
